import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  ArrowLeft,
  Images,
  Plus,
  Trash2,
  Copy,
  Send,
  ExternalLink,
  Upload,
  X,
  Calendar,
  Users,
  Mail
} from 'lucide-react';
import { toast } from 'sonner';
import { compressImage, postWithRetry } from '../../lib/uploadUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GalleryDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [gallery, setGallery] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [showAddPhotos, setShowAddPhotos] = useState(false);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [photoUrls, setPhotoUrls] = useState('');
  const [deletePhotoDialog, setDeletePhotoDialog] = useState({ open: false, photo: null });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailPreview, setEmailPreview] = useState({ open: false, html: '', subject: '', loading: false });
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    fetchGallery();
    fetchPhotos();
  }, [id]);

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/galleries/${id}`);
      setGallery(response.data);
    } catch (error) {
      toast.error('Failed to load gallery');
      navigate('/admin/galleries');
    } finally {
      setLoading(false);
    }
  };

  const fetchPhotos = async () => {
    try {
      const response = await axios.get(`${API}/galleries/${id}/photos`);
      setPhotos(response.data);
    } catch (error) {
      console.error('Failed to load photos');
    }
  };

  const handleAddPhotos = async () => {
    const urls = photoUrls.split('\n').filter(url => url.trim());
    if (urls.length === 0) {
      toast.error('Please enter at least one photo URL');
      return;
    }

    setUploading(true);
    try {
      const photosData = urls.map((url, index) => ({
        url: url.trim(),
        filename: `photo-${index + 1}.jpg`
      }));

      await axios.post(`${API}/photos/batch?gallery_id=${id}`, photosData);
      toast.success(`Added ${urls.length} photos`);
      setPhotoUrls('');
      setShowAddPhotos(false);
      fetchPhotos();
      fetchGallery();
    } catch (error) {
      toast.error('Failed to add photos');
    } finally {
      setUploading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    await uploadFiles(Array.from(files));
    event.target.value = '';
  };

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    
    // Filter only image files
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    if (imageFiles.length === 0) {
      toast.error('Please select image files (JPEG, PNG, WebP)');
      return;
    }

    setUploading(true);
    setUploadProgress({ current: 0, total: imageFiles.length });
    
    try {
      let uploaded = 0;
      let failed = 0;
      let dupSkipped = 0;
      const BATCH_SIZE = 5;
      
      // Compress and upload in small batches (faster, memory-safe, retry-protected)
      for (let i = 0; i < imageFiles.length; i += BATCH_SIZE) {
        const batch = imageFiles.slice(i, i + BATCH_SIZE);
        
        const formData = new FormData();
        formData.append('gallery_id', id);
        for (const file of batch) {
          const compressed = await compressImage(file);
          formData.append('files', compressed);
        }
        
        try {
          const response = await postWithRetry(`${API}/upload/photos-batch`, formData);
          uploaded += response.data.uploaded || 0;
          dupSkipped += response.data.skipped_duplicates || 0;
        } catch (error) {
          console.error('Batch upload failed:', error);
          failed += batch.length;
        }
        setUploadProgress({ current: Math.min(i + batch.length, imageFiles.length), total: imageFiles.length });
      }

      if (uploaded > 0) toast.success(`Uploaded ${uploaded} photos`);
      if (dupSkipped > 0) toast.warning(`${dupSkipped} duplicate photo(s) skipped (same filename already in this gallery)`);
      if (failed > 0) toast.error(`${failed} photos failed to upload - please retry those files`);
      setShowFileUpload(false);
      fetchPhotos();
      fetchGallery();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photos');
    } finally {
      setUploading(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging false if we're leaving the drop zone entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      uploadFiles(files);
    }
  };

  const openEmailPreview = async () => {
    if (!gallery?.client_email) {
      toast.error('No client email for this gallery');
      return;
    }
    setEmailPreview({ open: true, html: '', subject: '', loading: true });
    try {
      const res = await axios.post(`${API}/email-template/preview`, { gallery_id: id });
      setEmailPreview({ open: true, html: res.data.html, subject: res.data.subject, loading: false });
    } catch (error) {
      toast.error('Failed to load email preview');
      setEmailPreview({ open: false, html: '', subject: '', loading: false });
    }
  };

  const handleSendGalleryEmail = async () => {
    setSendingEmail(true);
    try {
      await axios.post(`${API}/email/send-gallery-notification?gallery_id=${id}`);
      toast.success(`Email sent to ${gallery.client_email}`);
      setEmailPreview({ open: false, html: '', subject: '', loading: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDeletePhoto = async () => {
    if (!deletePhotoDialog.photo) return;
    try {
      await axios.delete(`${API}/photos/${deletePhotoDialog.photo.id}`);
      toast.success('Photo deleted');
      setDeletePhotoDialog({ open: false, photo: null });
      fetchPhotos();
      fetchGallery();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const handlePublish = async () => {
    try {
      await axios.put(`${API}/galleries/${id}/status?status=published`);
      toast.success('Gallery published');
      fetchGallery();
    } catch (error) {
      toast.error('Failed to publish gallery');
    }
  };

  const copyAccessLink = () => {
    const link = `${window.location.origin}/gallery/${gallery.access_code}`;
    navigator.clipboard.writeText(link);
    toast.success('Gallery link copied to clipboard');
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-slate-200 rounded" />
        <div className="h-64 bg-slate-200 rounded-xl" />
      </div>
    );
  }

  if (!gallery) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/galleries')} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-heading font-bold text-slate-900">{gallery.name}</h1>
              <Badge className={gallery.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
                {gallery.status}
              </Badge>
            </div>
            <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
              <span className="flex items-center gap-1">
                <Images className="h-4 w-4" />
                {gallery.photo_count} photos
              </span>
              {gallery.event_date && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {new Date(gallery.event_date).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={copyAccessLink} data-testid="copy-link-btn">
            <Copy className="h-4 w-4 mr-2" />
            Copy Link
          </Button>
          <Link to={`/gallery/${gallery.access_code}`} target="_blank">
            <Button variant="outline" data-testid="preview-btn">
              <ExternalLink className="h-4 w-4 mr-2" />
              Preview
            </Button>
          </Link>
          {gallery.client_email && gallery.status === 'published' && (
            <Button 
              variant="outline" 
              onClick={openEmailPreview}
              disabled={sendingEmail}
              data-testid="send-email-btn"
            >
              <Mail className="h-4 w-4 mr-2" />
              {sendingEmail ? 'Sending...' : 'Send Email'}
            </Button>
          )}
          {gallery.status === 'draft' && (
            <Button className="btn-primary" onClick={handlePublish} data-testid="publish-btn">
              <Send className="h-4 w-4 mr-2" />
              Publish
            </Button>
          )}
        </div>
      </div>

      {/* Client Info */}
      {(gallery.client_name || gallery.client_email) && (
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-6">
              {gallery.client_name && (
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-slate-400" />
                  <span className="font-medium">{gallery.client_name}</span>
                </div>
              )}
              {gallery.client_email && (
                <div className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-slate-400" />
                  <span>{gallery.client_email}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Access Code Card */}
      <Card className="border-slate-200 bg-slate-50">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 mb-1">Access Code</p>
              <code className="text-2xl font-mono font-bold text-slate-900">{gallery.access_code}</code>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-600 mb-1">Share Link</p>
              <p className="text-sm text-blue-600 font-medium">
                {window.location.origin}/gallery/{gallery.access_code}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Photos Section */}
      <Card className="border-stone-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-xl text-stone-800">Photos ({photos.length})</CardTitle>
          <div className="flex gap-2">
            <Button onClick={() => setShowAddPhotos(true)} variant="outline" data-testid="add-photos-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add URLs
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Drag and Drop Upload Zone */}
          <div
            className={`mb-6 border-2 border-dashed rounded-lg p-8 text-center transition-all cursor-pointer ${
              isDragging 
                ? 'border-amber-500 bg-amber-50' 
                : uploading 
                  ? 'border-blue-300 bg-blue-50' 
                  : 'border-slate-300 hover:border-amber-400 hover:bg-amber-50/50'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !uploading && document.getElementById('photo-upload-input').click()}
          >
            <input
              id="photo-upload-input"
              type="file"
              multiple
              accept="image/*"
              className="hidden"
              onChange={handleFileUpload}
              disabled={uploading}
            />
            {uploading ? (
              <div className="space-y-3">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-600 mx-auto" />
                <p className="text-amber-700 font-medium">
                  Uploading {uploadProgress.current} of {uploadProgress.total} photos...
                </p>
                <p className="text-sm text-slate-500">Please wait, this may take a moment for large files</p>
              </div>
            ) : isDragging ? (
              <div className="space-y-2">
                <Upload className="h-12 w-12 text-amber-500 mx-auto" />
                <p className="text-lg font-medium text-amber-700">Drop photos here!</p>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload className="h-10 w-10 text-slate-400 mx-auto" />
                <p className="text-slate-600 font-medium">Drag & drop photos here</p>
                <p className="text-sm text-slate-400">or click to browse • JPEG, PNG, WebP supported</p>
                <p className="text-xs text-slate-400 mt-2">Images will be automatically resized to 4000px max</p>
              </div>
            )}
          </div>

          {photos.length > 0 ? (
            <div className="masonry-grid">
              {photos.map((photo) => {
                const imgSrc = photo.thumbnail_url 
                  ? `${process.env.REACT_APP_BACKEND_URL}${photo.thumbnail_url}` 
                  : photo.url?.startsWith('http') 
                    ? photo.url 
                    : `${process.env.REACT_APP_BACKEND_URL}${photo.url}`;
                return (
                  <div key={photo.id} className="masonry-item group relative">
                    <img
                      src={imgSrc}
                      alt={photo.filename}
                      className="w-full rounded-lg"
                      loading="lazy"
                    />
                    <div className="photo-overlay rounded-lg flex items-end justify-end p-2">
                      <Button
                        variant="destructive"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setDeletePhotoDialog({ open: true, photo })}
                        data-testid={`delete-photo-${photo.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : !uploading && (
            <div className="text-center py-8 text-slate-500">
              <Images className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No photos yet. Drag and drop images above to get started!</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Photos Dialog */}
      <Dialog open={showAddPhotos} onOpenChange={setShowAddPhotos}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Photos</DialogTitle>
            <DialogDescription>
              Enter photo URLs, one per line. These can be from any image hosting service.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <textarea
              className="w-full h-48 p-3 border border-slate-200 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="https://example.com/photo1.jpg&#10;https://example.com/photo2.jpg&#10;https://example.com/photo3.jpg"
              value={photoUrls}
              onChange={(e) => setPhotoUrls(e.target.value)}
              data-testid="photo-urls-input"
            />
            <p className="text-xs text-slate-500">
              Tip: You can paste multiple URLs at once, separated by new lines.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPhotos(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-blue-600 hover:bg-blue-700" 
              onClick={handleAddPhotos}
              disabled={uploading}
              data-testid="submit-photos-btn"
            >
              {uploading ? 'Adding...' : 'Add Photos'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Photo Dialog */}
      <Dialog open={deletePhotoDialog.open} onOpenChange={(open) => setDeletePhotoDialog({ open, photo: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Photo</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this photo? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletePhotoDialog({ open: false, photo: null })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeletePhoto} data-testid="confirm-delete-photo-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Email Preview Dialog */}
      <Dialog open={emailPreview.open} onOpenChange={(open) => { if (!open) setEmailPreview({ open: false, html: '', subject: '', loading: false }); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="email-preview-dialog">
          <DialogHeader>
            <DialogTitle>Preview Email</DialogTitle>
            <DialogDescription>
              This is exactly what {gallery?.client_email} will receive. You can edit the template on the Bulk Email page.
            </DialogDescription>
          </DialogHeader>
          {emailPreview.loading ? (
            <p className="text-sm text-slate-500 p-4">Loading preview...</p>
          ) : (
            <div className="space-y-3">
              <div className="bg-slate-50 rounded p-3">
                <p className="text-xs font-medium text-slate-500 mb-1">Subject:</p>
                <p className="text-slate-800 text-sm" data-testid="single-preview-subject">{emailPreview.subject}</p>
              </div>
              <iframe
                title="Email Preview"
                srcDoc={emailPreview.html}
                className="w-full bg-white rounded border border-slate-200"
                style={{ height: '420px' }}
                data-testid="single-email-preview-iframe"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailPreview({ open: false, html: '', subject: '', loading: false })}>
              Cancel
            </Button>
            <Button onClick={handleSendGalleryEmail} disabled={sendingEmail || emailPreview.loading} data-testid="confirm-send-email-btn">
              <Mail className="h-4 w-4 mr-2" />
              {sendingEmail ? 'Sending...' : 'Send Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GalleryDetailPage;
